import {customElement, property} from 'lit/decorators.js';
import {css, CSSResult, html, PropertyValues, TemplateResult, unsafeCSS,} from 'lit';
import {consume} from '@lit/context';
import styles from './collection-action-menu.scss';
import {CollectionTrackingContext, WishlistCollection} from '../../../@types/wishlist.js';
import typographyStyles from '../../../styles/responsive-nui-typography.scss';
import backofficesHide from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-16_eye-closed.svg';
import backofficesShow from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-16_eye.svg';
import '../action-menu/action-menu-item.js';
import {ActionItem, ActionMenu} from '../action-menu/action-menu.js';
import {collectionsContext} from '../../../scripts/context/wishlistContext.js';
import {CollectionsState} from '../../../scripts/context/wishlist-state.js';
import {trackCollectionActionMenuOpen} from '../../../scripts/service/cube-tracking-layer.js';
import {collectionMenuContext} from '../../../scripts/context/menuContext.js';
import {CollectionMenuState} from '../../../scripts/context/menu-state.js';
import {addPresentationToIcon} from '../icon/icon-accessibility.js';

@customElement('ents-collection-action-menu')
export class CollectionActionMenu extends ActionMenu {
  protected testId: string = 'ents-collection-action-menu';

  @consume({context: collectionMenuContext, subscribe: true})
  @property({attribute: false})
  private collectionMenuState!: CollectionMenuState;

  @consume({context: collectionsContext, subscribe: true})
  @property({attribute: false})
  private collectionsState!: CollectionsState;

  static get styles(): CSSResult[] {
    return [css`${unsafeCSS(styles)}`, css`${unsafeCSS(typographyStyles)}`];
  }

  protected updateStateOnClose() {
    if (this.collectionMenuState.context.openActionMenu) {
      this.collectionMenuState.closeActionMenu();
    }
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);

    const previousActionMenuState: CollectionMenuState | undefined = _changedProperties.get('collectionMenuState');
    if (previousActionMenuState != null
      && !previousActionMenuState.context.openActionMenu
      && this.collectionMenuState.context.openActionMenu
      && this.collectionMenuState.context.collection
    ) {
      this.trackOpenEvent();
      this.showLayer = true;
    }
  }

  private open() {
    this.collectionMenuState.openActionMenu(this.collectionMenuState.context.collection!, this.collectionMenuState.context.collectionTrackingContext!);
  }

  protected getApplicableActionItems() {
    const collection = this.collectionMenuState.context.collection!;
    const collectionTrackingContext = this.collectionMenuState.context.collectionTrackingContext!;
    return [
      this.getRenameList(collection, collectionTrackingContext),
      this.getShareItem(collection, collectionTrackingContext),
      this.getRemoveItem(collection, collectionTrackingContext),
    ];
  }

  private getRemoveItem(
    collection: WishlistCollection,
    collectionTrackingContext: CollectionTrackingContext
  ): ActionItem {
    return {
      label: this.messages['ents.wishlist.collection.actionmenu.delete']!,
      arrow: false,
      trackingKey: 'remove',
      callback: () =>
        this.collectionMenuState.openRemoveCollectionView(
          collection,
          collectionTrackingContext,
          this.open.bind(this)
        ),
    };
  }

  private getShareItem(
    collection: WishlistCollection,
    collectionTrackingContext: CollectionTrackingContext
  ): ActionItem {
    return {
      label: html`${this.messages['ents.wishlist.collection.actionmenu.share']} ${this.renderShareState(collection)}`,
      arrow: true,
      trackingKey: 'share',
      callback: () =>
        this.collectionMenuState.openShareView(
          collection,
          collectionTrackingContext,
          this.open.bind(this)
        ),
    };
  }

  private renderShareState(collection: WishlistCollection) {
    if (collection?.isSharingAllowed) {
      return html`
        <div class="ents-copy-small ents-wishlist-collection-action-menu__share-icon">
          ${addPresentationToIcon(backofficesShow())} ${this.messages['ents.wishlist.collection.share.state.shared']}
        </div>`;
    } else {
      return html`
        <div class="ents-copy-small ents-wishlist-collection-action-menu__share-icon">
          ${addPresentationToIcon(backofficesHide())} ${this.messages['ents.wishlist.collection.share.state.private']}
        </div>`;
    }
  }

  private getRenameList(
    collection: WishlistCollection,
    collectionTrackingContext: CollectionTrackingContext
  ): ActionItem {
    return {
      label: this.messages['ents.wishlist.collection.actionmenu.rename']!,
      arrow: true,
      trackingKey: 'rename',
      callback: () => this.collectionMenuState.openRenameLayer(
        collection,
        collectionTrackingContext,
        this.open.bind(this)
      ),
    };
  }

  private trackOpenEvent() {
    const items = this.getApplicableActionItems().map(item => item.trackingKey);
    const position = this.collectionsState.entities.indexOf(this.collectionMenuState.context.collection!) + 1;
    trackCollectionActionMenuOpen(
      items,
      this.collectionMenuState.context.collection!,
      position,
      this.collectionMenuState.context.collectionTrackingContext!.productsAmount
    );
  }

  protected renderInformation(): TemplateResult | symbol {
    return html`<h2 slot="title">${this.collectionMenuState.context.collection?.name}</h2>`;
  }
}
